/**
 * Utility functions for i18n and locale handling
 */

export const supportedLocales = ['el', 'en'] as const;
export type Locale = typeof supportedLocales[number];

/**
 * Get locale from pathname
 * @param pathname - The pathname to extract locale from (e.g., "/en/about" or "/about" for Greek)
 * @returns The locale or 'el' as default (Greek never appears in URLs)
 */
export function getLocaleFromPath(pathname: string): Locale {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (firstSegment && supportedLocales.includes(firstSegment as Locale)) {
    return firstSegment as Locale;
  }
  
  return 'el';
}

/**
 * Get pathname without locale prefix
 * @param pathname - The pathname to strip locale from
 * @returns The pathname without locale prefix
 */
export function getPathnameWithoutLocale(pathname: string): string {
  const locale = getLocaleFromPath(pathname);
  const pathWithoutLocale = pathname.replace(`/${locale}`, '') || '/';
  return pathWithoutLocale;
}

/**
 * Add locale prefix to pathname
 * @param pathname - The pathname to add locale to
 * @param locale - The locale to add
 * @returns The pathname with locale prefix (Greek never gets /el prefix, English gets /en prefix)
 */
export function addLocaleToPath(pathname: string, locale: Locale): string {
  const cleanPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const pathWithoutLocale = getPathnameWithoutLocale(cleanPath);
  
  // Greek (el) is the default locale and MUST NOT appear in URLs
  // Return path without any locale prefix for Greek
  if (locale === 'el') {
    return pathWithoutLocale;
  }
  
  // English (en) MUST be prefixed with /en
  return `/${locale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`;
}

